// deps
import React, { ReactElement, memo, useCallback } from "react";
import classNames from "classnames";

// internal deps
import { VALID_WALLET_LINK_STATUSES, isEmpty } from "@swiggy-private/payment-core";
import { AriaHidden, Tick } from "@swiggy-private/core-ui";
import { TextButton } from "@swiggy-private/dls-web-core/dist/components/TextButton";

// helpers
import { isAmazonWallet, isWalletTokenExpired } from "@payment-ui/helpers/Wallet";
import { getPaymentMethodPromoMsg } from "@payment-ui/helpers/Payment";

// interfaces
import { WalletPaymentMethodData, IWalletPaymentGroup } from "@payment-ui/interfaces/Wallet";

// components
import CircleLoader from "@payment-ui/widgets/circle-loader";
import { LinkAccount } from "@payment-ui/components/link-account";

// constants
import { TEXTS } from "@payment-ui/constants/index";

// data-testids
import { WalletTestIds } from "@swiggy-private/payment-test-ids";

import styles from "../Wallet.v4.scss";

interface WalletBalanceProps {
    walletBalanceLoading: boolean;
    selected: boolean;
    methodData: WalletPaymentMethodData;
    actions: IWalletPaymentGroup["paymentActions"];
    linkStatus: VALID_WALLET_LINK_STATUSES;
    hasLessBalance: boolean;
    balance: number | string;
    walletTestIds: WalletTestIds;
}

function WalletBalance(props: WalletBalanceProps): ReactElement {
    const { methodData, actions, walletTestIds, selected, walletBalanceLoading, linkStatus, hasLessBalance, balance } =
        props;

    const { refetchBalance } = actions;

    const promoMsg = getPaymentMethodPromoMsg(methodData);
    const balanceContainer = classNames({
        [styles.walletLinkAccount]: true,
        [styles.walletLinkAccountWithPromo]: !isEmpty(promoMsg),
        [styles.walletLinkAccountWithSubText]:
            !isEmpty(promoMsg) ||
            (linkStatus === VALID_WALLET_LINK_STATUSES.LINKED && (hasLessBalance || isWalletTokenExpired(linkStatus))),
    });

    const { state: amazonPaySdkStatus, sdkFetchInProgress } = actions.getAmazonPaySdkStatus();
    const isAPayWallet = isAmazonWallet(methodData);
    const aPaySdkAvailable = (amazonPaySdkStatus && amazonPaySdkStatus.available) || false;

    const refetchBalanceClick = useCallback(() => {
        refetchBalance(methodData);
    }, [refetchBalance, methodData]);

    const renderBalanceContent = (): ReactElement | null => {
        if (isAPayWallet) {
            if (sdkFetchInProgress || !amazonPaySdkStatus) {
                return (
                    <CircleLoader
                        size="medium"
                        className={styles.walletLinkAccountLoader}
                        data-testid={walletTestIds.CIRCLE_LOADER}
                    />
                );
            }

            if (aPaySdkAvailable) {
                return (
                    <AriaHidden as="div" className={styles.walletLinkAccountBalance}>
                        {!isEmpty(amazonPaySdkStatus.balance?.toString()) && (
                            <div
                                className={styles.walletLinkAccountBalanceValue}
                                data-testid={walletTestIds.WALLET_BALANCE}
                            >
                                {amazonPaySdkStatus.balance}
                            </div>
                        )}
                        {amazonPaySdkStatus.linked ? (
                            <Tick selected={selected} data-testid={walletTestIds.TICK} />
                        ) : (
                            <TextButton
                                textAlign="right"
                                buttonSize="small"
                                color="primary"
                                data-testid={walletTestIds.LINK_ACCOUNT}
                            >
                                {TEXTS.LINK_ACCOUNT}
                            </TextButton>
                        )}
                    </AriaHidden>
                );
            } else {
                return (
                    <div className={styles.walletLinkAccountBalance}>
                        <Tick selected={selected} data-testid={walletTestIds.TICK} />
                    </div>
                );
            }
        }

        if (walletBalanceLoading) {
            return (
                <CircleLoader
                    size="medium"
                    className={styles.walletLinkAccountLoader}
                    data-testid={walletTestIds.CIRCLE_LOADER}
                />
            );
        }

        switch (linkStatus) {
            case VALID_WALLET_LINK_STATUSES.LINKED:
                return (
                    <AriaHidden as="div" className={styles.walletLinkAccountBalance}>
                        <div
                            className={styles.walletLinkAccountBalanceValue}
                            data-testid={walletTestIds.WALLET_BALANCE}
                        >
                            {balance}
                        </div>
                        <Tick selected={selected} data-testid={walletTestIds.TICK} />
                    </AriaHidden>
                );
            case VALID_WALLET_LINK_STATUSES.RETRY:
                return (
                    <TextButton
                        textAlign="right"
                        buttonSize="small"
                        color="primary"
                        onClick={refetchBalanceClick}
                        data-testid={walletTestIds.RETRY_LINK_ACCOUNT}
                    >
                        {TEXTS.RETRY}
                    </TextButton>
                );
            default:
                return (
                    <div className={styles.walletLinkAccountContainer}>
                        <LinkAccount data-testid={walletTestIds.LINK_ACCOUNT} />
                    </div>
                );
        }
    };

    return <div className={balanceContainer}>{renderBalanceContent()}</div>;
}

export default memo(WalletBalance);
